import React, { useState } from 'react';
import { Alert, Box, Card, CardContent, Chip, Stack, Typography } from '@mui/joy';
import type { IconType } from 'react-icons';
import {
  MdCategory,
  MdConstruction,
  MdCropPortrait,
  MdDoorFront,
  MdHandyman,
  MdHomeWork,
  MdLayers,
  MdRoofing,
  MdWindow,
} from 'react-icons/md';
import { IconWrapper } from '../../components/IconWrapper';
import type { Project } from '../../types/project';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';

export const PRODUCT_TYPE_OPTIONS = [
  'Glass',
  'Windows',
  'Secondary Glazing',
  'Doors',
  'Conservatory',
  'Warm Roof',
  'Building',
  'Repairs',
  'General',
] as const;

const PRODUCT_TYPE_ICONS: Record<string, IconType> = {
  Glass: MdCropPortrait,
  Windows: MdWindow,
  'Secondary Glazing': MdLayers,
  Doors: MdDoorFront,
  Conservatory: MdHomeWork,
  'Warm Roof': MdRoofing,
  Building: MdConstruction,
  Repairs: MdHandyman,
  General: MdCategory,
};

export function ProductTypeChips({
  selected,
  onToggle,
  readOnly = false,
  appearance = 'solid',
}: {
  selected: string[];
  onToggle?: (type: string) => void;
  readOnly?: boolean;
  /** solid = header reminder chips; outline = Products editor chips */
  appearance?: 'solid' | 'outline';
}) {
  return (
    <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
      {PRODUCT_TYPE_OPTIONS.map((type) => {
        const isSelected = selected.includes(type);
        const solid = appearance === 'solid';
        const bgcolor = isSelected
          ? (solid ? 'var(--jarvis-chip-selected-bg)' : 'var(--jarvis-chip-outline-selected-bg)')
          : (solid ? 'var(--jarvis-chip-bg)' : 'var(--jarvis-chip-outline-bg)');
        const color = isSelected
          ? (solid ? 'var(--jarvis-chip-selected-text)' : 'var(--jarvis-chip-outline-selected-text)')
          : (solid ? 'var(--jarvis-chip-text)' : 'var(--jarvis-chip-outline-text)');
        const borderColor = isSelected
          ? (solid ? 'var(--jarvis-chip-selected-bg)' : 'var(--jarvis-chip-outline-selected-border)')
          : 'var(--jarvis-stage-border)';
        return (
          <Chip
            key={type}
            size="sm"
            variant="plain"
            startDecorator={PRODUCT_TYPE_ICONS[type] ? <IconWrapper icon={PRODUCT_TYPE_ICONS[type]} /> : undefined}
            {...(readOnly
              ? {
                  component: 'span' as const,
                  title: isSelected ? type : `${type} (not selected)`,
                }
              : {
                  onClick: () => onToggle?.(type),
                  'aria-pressed': isSelected,
                })}
            sx={{
              bgcolor,
              color,
              border: '1px solid',
              borderColor,
              cursor: readOnly ? 'default' : 'pointer',
              '--Chip-decoratorColor': color,
            }}
          >
            {type}
          </Chip>
        );
      })}
    </Stack>
  );
}

interface WorkspaceProductTypesCardProps {
  project: Project;
  onProjectPatch: (patch: Partial<Project>) => Promise<void>;
}

export default function WorkspaceProductTypesCard({
  project,
  onProjectPatch,
}: WorkspaceProductTypesCardProps) {
  const selected = (project.product_types || []).map((type) => String(type).trim()).filter(Boolean);
  const [pending, setPending] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shown = pending ?? selected;

  const toggle = async (type: string) => {
    if (pending) return;
    const next = shown.includes(type)
      ? shown.filter((item) => item !== type)
      : [...shown, type];
    setPending(next);
    setError(null);
    try {
      await onProjectPatch({ product_types: next });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update products');
    } finally {
      setPending(null);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={MdCategory}
          title="Products"
          description="Select what this job includes. The same products stay at the top of the workspace as a reminder."
        />
        {error && <Alert color="danger" sx={{ mb: 1.25 }}>{error}</Alert>}
        <ProductTypeChips
          selected={shown}
          appearance="outline"
          onToggle={(type) => { void toggle(type); }}
        />
      </CardContent>
    </Card>
  );
}
